import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { logRender } from '../utils/debugLoop';

// Editor workspace theme definitions
export type EditorThemeId =
  | 'polished-dark'
  | 'matte-dark'
  | 'midnight'
  | 'deep-violet'
  | 'slate'
  | 'ocean-dark'
  | 'soft-light'
  | 'warm-sunrise'
  | 'mint-fresh'
  | 'sky-day';

export type EditorTheme = {
  id: EditorThemeId;
  name: string;
  isDark: boolean;
};

// Available editor themes (metadata only - styles are in CSS)
export const EDITOR_THEMES: EditorTheme[] = [
  { id: 'polished-dark', name: 'Polished Dark', isDark: true },
  { id: 'matte-dark', name: 'Matte Dark', isDark: true },
  { id: 'midnight', name: 'Midnight', isDark: true },
  { id: 'deep-violet', name: 'Deep Violet', isDark: true },
  { id: 'slate', name: 'Slate', isDark: true },
  { id: 'ocean-dark', name: 'Ocean Dark', isDark: true },
  { id: 'soft-light', name: 'Soft Light', isDark: false },
  { id: 'warm-sunrise', name: 'Warm Sunrise', isDark: false },
  { id: 'mint-fresh', name: 'Mint Fresh', isDark: false },
  { id: 'sky-day', name: 'Sky Day', isDark: false },
];

type ThemeContextType = {
  // App-level dark/light mode
  darkMode: boolean;
  toggleDarkMode: () => void;

  // Editor workspace theme
  editorTheme: EditorThemeId;
  setEditorTheme: (themeId: EditorThemeId) => void;
  editorThemes: EditorTheme[];
  currentEditorTheme: EditorTheme;

  // Theme enable/disable toggle
  editorThemeEnabled: boolean;
  setEditorThemeEnabled: (enabled: boolean) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Storage keys
const STORAGE_DARK_MODE = 'darkMode';
const STORAGE_EDITOR_THEME = 'vb-editor-theme';
const STORAGE_EDITOR_THEME_ENABLED = 'vb-editor-theme-enabled';

// Apply editor theme instantly via data attribute (CSS-only, no re-render)
function applyEditorTheme(themeId: EditorThemeId | null) {
  if (themeId) {
    document.documentElement.setAttribute('data-editor-theme', themeId);
  } else {
    document.documentElement.removeAttribute('data-editor-theme');
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage and system preference on initial load
    const saved = localStorage.getItem(STORAGE_DARK_MODE);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [editorTheme, setEditorThemeState] = useState<EditorThemeId>(() => {
    const saved = localStorage.getItem(STORAGE_EDITOR_THEME);
    if (saved && EDITOR_THEMES.some(t => t.id === saved)) {
      return saved as EditorThemeId;
    }
    return 'polished-dark'; // Default editor theme
  });

  const [editorThemeEnabled, setEditorThemeEnabledState] = useState<boolean>(false);

  logRender('ThemeProvider', undefined, { darkMode, editorTheme, editorThemeEnabled });

  // Apply dark/light mode
  useEffect(() => {
    localStorage.setItem(STORAGE_DARK_MODE, JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
  }, [darkMode]);

  // Apply editor theme on mount and changes (instant CSS-only update)
  useEffect(() => {
    // Studio-specific theme overrides are disabled; global dark/light mode drives all pages.
    applyEditorTheme(null);
    localStorage.setItem(STORAGE_EDITOR_THEME, editorTheme);
    localStorage.setItem(STORAGE_EDITOR_THEME_ENABLED, JSON.stringify(false));
    if (editorThemeEnabled) {
      setEditorThemeEnabledState(false);
    }
  }, [editorTheme, editorThemeEnabled]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev: boolean) => !prev);
  }, []);

  const setEditorTheme = useCallback((themeId: EditorThemeId) => {
    // Instant update - just change the data attribute (only if enabled)
    if (editorThemeEnabled) {
      applyEditorTheme(themeId);
    }
    // Then update state for persistence
    setEditorThemeState(themeId);
  }, [editorThemeEnabled]);

  const setEditorThemeEnabled = useCallback((_enabled: boolean) => {
    // Keep this API for compatibility, but do not allow enabling Studio-only theme overrides.
    setEditorThemeEnabledState(false);
    applyEditorTheme(null);
    localStorage.setItem(STORAGE_EDITOR_THEME_ENABLED, JSON.stringify(false));
  }, []);

  // Get current theme metadata
  const currentEditorTheme = useMemo(() => {
    return EDITOR_THEMES.find(t => t.id === editorTheme) || EDITOR_THEMES[0];
  }, [editorTheme]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    darkMode,
    toggleDarkMode,
    editorTheme,
    setEditorTheme,
    editorThemes: EDITOR_THEMES,
    currentEditorTheme,
    editorThemeEnabled,
    setEditorThemeEnabled,
  }), [darkMode, toggleDarkMode, editorTheme, setEditorTheme, currentEditorTheme, editorThemeEnabled, setEditorThemeEnabled]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Utility hook for components that only need editor theme
export function useEditorTheme() {
  const { editorTheme, setEditorTheme, editorThemes, currentEditorTheme, editorThemeEnabled, setEditorThemeEnabled } = useTheme();
  return { editorTheme, setEditorTheme, editorThemes, currentEditorTheme, editorThemeEnabled, setEditorThemeEnabled };
}
